import { on, post, notifier, prefersReducedMotion } from '../util';
import { renderChart, findChartById, EDITABLE_CHART_IDS, rendersAsChart } from '../home';
import * as Highcharts from 'highcharts';

// The chart loop's injected global (var data_array = {$graph_data->data_array}; in
// Home/default.latte's page_js block) — the server-applied SAVED-ORDER reference used to
// position a freshly un-hidden chart's wrapper (D-15). Object.keys() minus 'yoy_enabled'.
declare const data_array: Record<string, any>;

const HEIGHT_MIN = 220;
const HEIGHT_MAX = 800;
const HEIGHT_DEFAULT = 320;

// Enter/leave animation duration — MUST match the .graph-*-active transition in style.scss.
const ANIM_MS = 220;

// Idempotency guard (MEMORY: shared/late-bound init must be idempotent) — home.ts's static
// init calls initCustomize() once, but guard anyway against any future double-invocation.
let _customizeBound = false;

export function initCustomize(): void {
    if (_customizeBound) return;
    _customizeBound = true;

    bindWidthControls();
    bindHeightSlider();
    bindResizeHandle();
    bindHideControls();
    bindShowAllToggle();
    bindReset();
}

// ── Shared height (D-07/D-08) ────────────────────────────────────────────────

let rafPending = false;

/** Coalesces reflow calls into AT MOST one per animation frame (D-08, anti-pattern #4). */
function scheduleReflow(): void {
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(() => {
        const reduced = prefersReducedMotion();
        (Highcharts.charts as any[]).forEach((chart: any) => {
            if (!chart) return;
            if (reduced) {
                // Suppress Highcharts' internal resize easing (D-08 reduced-motion).
                chart.update({ chart: { animation: false } }, false);
            }
            chart.reflow();
        });
        rafPending = false;
    });
}

/** Reads the live --chart-height custom property off .col-charts. */
function currentChartHeight(): number {
    const container = document.querySelector('.col-charts') as HTMLElement | null;
    if (!container) return HEIGHT_DEFAULT;
    const raw = getComputedStyle(container).getPropertyValue('--chart-height').trim();
    const parsed = parseInt(raw, 10);
    return isNaN(parsed) ? HEIGHT_DEFAULT : parsed;
}

/**
 * Single source of truth for both the panel slider AND the resize handle (D-07): clamps,
 * writes the shared --chart-height var, syncs every readout (#dash-height-value, slider,
 * every .chart-resize-handle's aria), and schedules a coalesced reflow. Returns the clamped
 * value so callers can persist it.
 */
function applyChartHeight(px: number): number {
    const clamped = Math.min(HEIGHT_MAX, Math.max(HEIGHT_MIN, Math.round(px)));

    const container = document.querySelector('.col-charts') as HTMLElement | null;
    if (container) {
        container.style.setProperty('--chart-height', clamped + 'px');
    }

    const valueEl = document.getElementById('dash-height-value');
    if (valueEl) valueEl.textContent = clamped + 'px';

    const slider = document.getElementById('dash-height-slider') as HTMLInputElement | null;
    if (slider) {
        slider.value = String(clamped);
        slider.setAttribute('aria-valuetext', clamped + 'px');
    }

    document.querySelectorAll('.chart-resize-handle').forEach((handle) => {
        handle.setAttribute('aria-valuenow', String(clamped));
        handle.setAttribute('aria-valuetext', clamped + 'px');
    });

    scheduleReflow();
    return clamped;
}

function saveHeight(px: number): void {
    post('?do=saveHeight&height=' + px, {}, (data: any) => {
        if (data.status !== 1) {
            notifier.alert('Failed to save graph height');
        }
    });
}

function bindHeightSlider(): void {
    const slider = document.getElementById('dash-height-slider') as HTMLInputElement | null;
    if (!slider) return;
    // `change`, not `input` (anti-pattern #3) — fires once per drag/keystroke release.
    slider.addEventListener('change', () => {
        const clamped = applyChartHeight(parseInt(slider.value, 10));
        saveHeight(clamped);
    });
}

// ── Bottom-edge resize handle (D-07/D-08) — hand-rolled Pointer Events, no analog exists ────

let resizingHandle: HTMLElement | null = null;
let resizeStartY = 0;
let resizeStartHeight = HEIGHT_DEFAULT;
// IN-05: true only once a genuine pointermove drag has occurred between pointerdown and
// pointerup — a bare click (pointerdown immediately followed by pointerup, no move) must
// not persist an unchanged height.
let resizeMoved = false;

function bindResizeHandle(): void {
    // Delegated at document level (not per-element addEventListener) so a freshly un-hidden
    // chart's .chart-resize-handle (inserted after page load, D-15) works with zero re-bind.
    document.addEventListener('pointerdown', (e: PointerEvent) => {
        const target = e.target as HTMLElement | null;
        const handle = target ? (target.closest('.chart-resize-handle') as HTMLElement | null) : null;
        if (!handle) return;
        resizingHandle = handle;
        resizeStartY = e.clientY;
        resizeStartHeight = currentChartHeight();
        resizeMoved = false;
        document.body.classList.add('is-chart-resizing');
        handle.classList.add('is-resizing');
    });

    document.addEventListener('pointermove', (e: PointerEvent) => {
        if (!resizingHandle) return;
        const delta = e.clientY - resizeStartY;
        resizeMoved = true;
        applyChartHeight(resizeStartHeight + delta);
    }, { passive: true });

    document.addEventListener('pointerup', () => {
        if (!resizingHandle) return;
        const finalHeight = currentChartHeight();
        document.body.classList.remove('is-chart-resizing');
        resizingHandle.classList.remove('is-resizing');
        resizingHandle = null;
        // IN-05: persist on release only when a drag actually moved the height — never on a
        // no-op click.
        if (resizeMoved) {
            saveHeight(finalHeight);
        }
        resizeMoved = false;
    });

    // Keyboard alternative (a11y, required per UI-SPEC) — same delegation reasoning.
    document.addEventListener('keydown', (e: KeyboardEvent) => {
        const target = e.target as HTMLElement | null;
        const handle = target ? (target.closest('.chart-resize-handle') as HTMLElement | null) : null;
        if (!handle) return;

        let next: number;
        if (e.key === 'ArrowUp') next = currentChartHeight() + 10;
        else if (e.key === 'ArrowDown') next = currentChartHeight() - 10;
        else if (e.key === 'PageUp') next = currentChartHeight() + 50;
        else if (e.key === 'PageDown') next = currentChartHeight() - 50;
        else if (e.key === 'Home') next = HEIGHT_MIN;
        else if (e.key === 'End') next = HEIGHT_MAX;
        else return;

        e.preventDefault();
        const clamped = applyChartHeight(next);
        // No drag state on a keypress — reflow + save happen together, per keypress.
        saveHeight(clamped);
    });
}

// ── Per-graph width (HOME-05, P-09/P-10/P-11/P-13) ────────────────────────────
// Replaces the removed global 1/2-column layout control entirely. Two-way synced between the
// inline .chart-width-btn (on-card) and the offcanvas .js-graph-width radio pair, copying
// bindHideControls()'s .chart-hide-btn <-> .js-graph-visibility shape verbatim in structure.

// Bootstrap Icons arrows-angle-contract/arrows-angle-expand — hand-copied inline SVG (D-01, zero
// new npm package), same 16x16/currentColor convention as every other icon in this template.
// Static, hardcoded strings only — never assembled from a fetch payload (T-218-15).
const ICON_ARROWS_CONTRACT = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">'
    + '<path d="M.172 15.828a.5.5 0 0 0 .707 0l4.096-4.096V14.5a.5.5 0 1 0 1 0v-3.975a.5.5 0 0 0-.5-.5H1.5a.5.5 0 0 0 0 1h2.768L.172 15.121a.5.5 0 0 0 0 .707M15.828.172a.5.5 0 0 0-.707 0l-4.096 4.096V1.5a.5.5 0 1 0-1 0v3.975a.5.5 0 0 0 .5.5H14.5a.5.5 0 0 0 0-1h-2.768L15.828.879a.5.5 0 0 0 0-.707"/>'
    + '</svg>';
const ICON_ARROWS_EXPAND = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">'
    + '<path d="M5.828 10.172a.5.5 0 0 0-.707 0l-4.096 4.096V11.5a.5.5 0 0 0-1 0v3.975a.5.5 0 0 0 .5.5H4.5a.5.5 0 0 0 0-1H1.732l4.096-4.096a.5.5 0 0 0 0-.707m4.344-4.344a.5.5 0 0 0 .707 0l4.096-4.096V4.5a.5.5 0 1 0 1 0V.525a.5.5 0 0 0-.5-.5H11.5a.5.5 0 0 0 0 1h2.768l-4.096 4.096a.5.5 0 0 0 0 .707"/>'
    + '</svg>';

/** Copywriting Contract: action-based, mirrors .chart-hide-btn's "Hide {title} graph" convention. */
function widthButtonAriaLabel(title: string, width: string): string {
    return width === 'half' ? ('Set ' + title + ' to full width') : ('Set ' + title + ' to half width');
}

/** arrows-angle-expand when currently half (clicking expands to full); arrows-angle-contract when
 *  currently full (clicking contracts to half). */
function widthButtonIcon(width: string): string {
    return width === 'half' ? ICON_ARROWS_EXPAND : ICON_ARROWS_CONTRACT;
}

/** Resolves a graph's display title from the offcanvas checklist, which always renders every
 *  GRAPH_KEYS entry regardless of hidden/visible state — reuses graphTitleFor()'s existing
 *  label-lookup shape rather than parsing it back out of an aria-label string. */
function titleForKey(key: string): string {
    const checkbox = document.querySelector('.js-graph-visibility[data-id="' + key + '"]') as HTMLInputElement | null;
    return checkbox ? graphTitleFor(checkbox, key) : key;
}

/**
 * Single place a width change takes effect (both directions funnel through here): toggles the
 * card's grid-span class, updates the inline button's icon/aria-label, checks the matching
 * offcanvas radio (setting .checked does not fire 'change' — no sync loop), reflows every
 * Highcharts instance, and persists.
 */
function applyWidth(key: string, width: string): void {
    const wrapper = document.querySelector('.chart-wrapper[data-id="' + key + '"]') as HTMLElement | null;
    if (wrapper) {
        wrapper.classList.toggle('chart-wrapper--half', width === 'half');
    }

    const btn = document.querySelector('.chart-width-btn[data-id="' + key + '"]') as HTMLElement | null;
    if (btn) {
        btn.setAttribute('aria-label', widthButtonAriaLabel(titleForKey(key), width));
        btn.innerHTML = widthButtonIcon(width);
    }

    const radio = document.getElementById('dash-width-' + key + '-' + width) as HTMLInputElement | null;
    if (radio) radio.checked = true;

    // The card's grid span changed, but Highcharts sizes to its container at render time and does
    // NOT observe container-width changes — without this reflow the chart keeps its old width
    // until a full reload (the same UAT fix the removed global layout toggle already paid for).
    scheduleReflow();
    saveWidths();
}

function bindWidthControls(): void {
    // Inline button — dynamic delegation required: unhideGraph() (Pitfall 3) inserts a fresh
    // .chart-width-btn after page load, which must work with zero re-bind.
    on('click', '.chart-width-btn', (btn: HTMLElement) => {
        const key = btn.getAttribute('data-id');
        if (!key) return;
        const wrapper = document.querySelector('.chart-wrapper[data-id="' + key + '"]');
        const isHalf = !!wrapper && wrapper.classList.contains('chart-wrapper--half');
        applyWidth(key, isHalf ? 'full' : 'half');
    }, true);

    // Offcanvas radio pair — these are radios, not a checkbox: read input.value ('half'/'full'),
    // never input.checked.
    on('change', '.js-graph-width', (input: HTMLInputElement) => {
        const key = input.getAttribute('data-id');
        if (!key) return;
        applyWidth(key, input.value);
    });
}

/** Mirrors saveHidden()'s shape: half-width keys only, comma-joined — matches the encoding
 *  handleSaveWidths (plan 262-07) expects. Falls back to the .chart-wrapper--half DOM state if
 *  the offcanvas radios have not rendered for some reason. */
function saveWidths(): void {
    const half: string[] = [];
    const radios = document.querySelectorAll<HTMLInputElement>('.js-graph-width');
    if (radios.length > 0) {
        radios.forEach((radio) => {
            if (radio.checked && radio.value === 'half') {
                const key = radio.getAttribute('data-id');
                if (key) half.push(key);
            }
        });
    } else {
        document.querySelectorAll<HTMLElement>('.chart-wrapper.chart-wrapper--half').forEach((w) => {
            const key = w.dataset.id;
            if (key) half.push(key);
        });
    }
    post('?do=saveWidths&widths=' + encodeURIComponent(half.join(',')), {}, (data: any) => {
        if (data.status !== 1) {
            notifier.alert('Failed to save graph width');
        }
    });
}

// ── Hide / un-hide (D-04/D-05/D-06/D-15) ─────────────────────────────────────

function saveHidden(): void {
    const hidden: string[] = [];
    document.querySelectorAll<HTMLInputElement>('.js-graph-visibility').forEach((checkbox) => {
        if (!checkbox.checked) {
            const key = checkbox.getAttribute('data-id');
            if (key) hidden.push(key);
        }
    });
    post('?do=saveHiddenGraphs&hidden=' + encodeURIComponent(hidden.join(',')), {}, (data: any) => {
        if (data.status !== 1) {
            notifier.alert('Failed to save hidden graphs');
        }
    });
}

/** D-01 case 2 / WR-03: persist which empty-in-range graphs the user has explicitly force-shown
 *  (marked data-forced) so their empty-state cards survive a reload. DOM-derived like saveHidden();
 *  only wrappers that actually hold an empty card count (a graph that later gained data is not forced). */
function saveForced(): void {
    const forced: string[] = [];
    document.querySelectorAll<HTMLElement>('.chart-wrapper[data-forced="1"]').forEach((w) => {
        if (!w.querySelector('.chart.chart-empty')) return;
        const key = w.dataset.id;
        if (key) forced.push(key);
    });
    post('?do=saveForcedGraphs&forced=' + encodeURIComponent(forced.join(',')), {}, (data: any) => {
        if (data.status !== 1) {
            notifier.alert('Failed to save shown-empty graphs');
        }
    });
}

function bindHideControls(): void {
    // Chart `×` — dynamic delegation required: un-hide (D-15) inserts a fresh
    // .chart-hide-btn after page load, which must work with zero re-bind.
    on('click', '.chart-hide-btn', (btn: HTMLElement) => {
        const key = btn.getAttribute('data-id');
        if (!key) return;
        hideGraph(key);
    }, true);

    // Panel checklist — unchecked = hide, checked = un-hide (D-15 on-demand fetch).
    on('change', '.js-graph-visibility', (checkbox: HTMLInputElement) => {
        const key = checkbox.getAttribute('data-id');
        if (!key) return;
        if (checkbox.checked) {
            unhideGraph(key, graphTitleFor(checkbox, key));
        } else {
            hideGraph(key);
        }
    });
}

function graphTitleFor(checkbox: HTMLInputElement, fallbackKey: string): string {
    const label = checkbox.id ? document.querySelector('label[for="' + checkbox.id + '"]') : null;
    return (label && label.textContent) ? label.textContent.trim() : fallbackKey;
}

/**
 * Runs `done()` once, after `el` finishes its CSS enter/leave transition (or immediately when
 * motion is reduced). transitionend fires per-property (opacity + transform) so it is guarded;
 * a timeout backstops the case where the transition never fires (e.g. tab backgrounded).
 */
function afterTransition(el: HTMLElement, done: () => void): void {
    let finished = false;
    const end = () => {
        if (finished) return;
        finished = true;
        el.removeEventListener('transitionend', end);
        done();
    };
    el.addEventListener('transitionend', end);
    window.setTimeout(end, ANIM_MS + 80);
}

/** D-06: literal DOM removal (never display:none — anti-pattern #2), no reload. Fades + slides
 *  the panel out first (D-08 reduced-motion → instant), then destroys the chart and removes it. */
function hideGraph(key: string): void {
    const wrapper = document.querySelector('.chart-wrapper[data-id="' + key + '"]') as HTMLElement | null;
    // WR-02: capture the chart instance UP FRONT, before the fade transition — re-resolving
    // by id inside the deferred finalize() (previous shape) raced a rapid un-hide/show-all
    // re-insert of a fresh same-id node within the ~300ms fade window (two nodes share one id).
    const chart = findChartById('chart_' + key);

    const checkbox = document.querySelector('.js-graph-visibility[data-id="' + key + '"]') as HTMLInputElement | null;
    if (checkbox && checkbox.checked) checkbox.checked = false;

    const finalize = () => {
        if (chart) chart.destroy();
        if (wrapper) wrapper.remove();
        saveHidden();
        saveForced(); // WR-03: a hidden graph is no longer force-shown — drop it from the forced set
    };

    if (!wrapper || prefersReducedMotion()) {
        finalize();
        return;
    }

    wrapper.classList.add('graph-leave-active');
    afterTransition(wrapper, finalize);
}

/**
 * D-15: on-demand fetch + in-place re-render — NO page reload. Fetches the ONE graph's
 * render payload, builds+inserts its .chart-wrapper at the saved-order position, and
 * instantiates its chart via home.ts's shared renderChart() builder.
 */
function unhideGraph(key: string, title: string, forcePersist = true, skipSave = false): void {
    const checkbox = document.querySelector('.js-graph-visibility[data-id="' + key + '"]') as HTMLInputElement | null;
    const statusEl = showInlineStatus(key, 'Showing ' + title + '…');

    post('?do=fetchGraphData&key=' + encodeURIComponent(key), {}, (data: any) => {
        if (statusEl) statusEl.remove();

        if (!data.success || !data.graph) {
            notifier.alert('Failed to show graph');
            if (checkbox) checkbox.checked = false;
            return;
        }

        // Reset path (forcePersist=false): a genuinely-empty graph returns to its auto-hidden
        // default — no card, no forced-persist. saveHidden() records it left the hidden set
        // (skipped during a reset batch — skipSave — since resetGraphSettings already
        // persisted the empty hidden set server-side; 218-REVIEW IN-04). Phase 282 gap closure
        // (QUICK-06 / GAP 1): a goal-forced graph is NOT genuinely empty — rendersAsChart()
        // decides whether the reset path applies, not the raw empty_in_range flag.
        if (!rendersAsChart(data.graph) && !forcePersist) {
            if (!skipSave) saveHidden();
            return;
        }

        const wrapper = buildChartWrapper(key, data.graph, currentChartHeight());
        const animate = !prefersReducedMotion();
        // Start hidden BEFORE insertion so the chart never flashes at full opacity (the class
        // sets opacity:0 + transform; neither affects layout, so renderChart still sizes right).
        if (animate) wrapper.classList.add('graph-enter');
        insertWrapperAtOrderPosition(wrapper, key);
        // Delegated ×/resize handlers (on(...,dynamic=true) + the document-level pointer/keydown
        // listeners above) and SortableJS' container-tracking apply automatically to this new
        // node — no per-node re-bind needed.
        // WR-03/D-01 case 2, updated by Phase 282 gap closure (QUICK-06 / GAP 1): buildChartWrapper()
        // already renders the empty-state clone into the .chart div when rendersAsChart() is false
        // — never instantiate Highcharts over it. A goal-forced graph DOES get a live chart here
        // even though empty_in_range is true.
        if (rendersAsChart(data.graph)) {
            renderChart(key, data.graph);
        } else {
            // WR-03/D-01 case 2: a manual un-hide of an empty graph is a persisted "force-show"
            // intent — mark the card so show-all-OFF and reset leave it in place across reloads.
            wrapper.dataset.forced = '1';
        }

        if (animate) {
            void wrapper.offsetWidth; // commit the start state, then transition to visible next frame
            requestAnimationFrame(() => {
                wrapper.classList.remove('graph-enter');
                wrapper.classList.add('graph-enter-active');
                afterTransition(wrapper, () => wrapper.classList.remove('graph-enter-active'));
            });
        }

        if (!skipSave) saveHidden();
        // Phase 282 gap closure (QUICK-06 / GAP 1): only persist a GENUINE operator force-show —
        // empty in range AND the server did not force it via a resolved goal target. A
        // server-forced chart is shown by policy, not by operator intent; persisting it here
        // would leave a stale forced key behind the day the operator deletes the goal.
        // Deliberately NOT rendersAsChart() — this is a different question ("should this be
        // remembered as forced") from "does this render as a chart".
        if (data.graph.empty_in_range && !data.graph.goal_forced) saveForced();
    });
}

// ── Show all graphs (D-04/D-05) ──────────────────────────────────────────────

/** Mirrors saveHidden()'s existing DOM-derived read (no new global) — D-05's "show-all never
 *  overrides an explicit user-hide" enforcement point. */
function currentlyHiddenKeys(): Set<string> {
    const hidden = new Set<string>();
    document.querySelectorAll<HTMLInputElement>('.js-graph-visibility').forEach((checkbox) => {
        if (!checkbox.checked) {
            const key = checkbox.getAttribute('data-id');
            if (key) hidden.add(key);
        }
    });
    return hidden;
}

function bindShowAllToggle(): void {
    on('change', '.js-show-all-toggle', (input: HTMLInputElement) => {
        applyShowAll(input.checked);
    });
}

/**
 * D-04/D-05: pure client-side card insert/remove over the already-loaded data_array — DASH-07
 * keeps every non-hidden graph's full series client-side already, so the visual toggle needs
 * NO new fetch. The persist POST below is the ONLY network call this makes. Graphs that
 * already have data are unaffected (they're shown regardless of show-all); only graphs that do
 * NOT render as a chart (rendersAsChart() false) are inserted/removed here, and only via
 * buildChartWrapper()'s <template>-clone branch — renderChart() is never called for one of those
 * (Pitfall 3). Phase 282 gap closure (QUICK-06 / GAP 1): a goal-forced graph (empty_in_range but
 * rendersAsChart() true) is skipped entirely by this function — show-all must never remove or
 * re-insert a chart the server already rendered live. User-hidden keys are skipped outright (D-05).
 */
function applyShowAll(checked: boolean): void {
    if (typeof data_array === 'undefined') return;
    const hidden = currentlyHiddenKeys();

    Object.entries(data_array)
        .filter(([key]) => key !== 'yoy_enabled')
        .forEach(([key, graph]) => {
            if (hidden.has(key)) return; // D-05: show-all never overrides an explicit user-hide
            // graphs with data are already shown; goal-forced graphs render as a live chart and
            // must never be toggled by show-all (QUICK-06 / GAP 1) — rendersAsChart() covers both.
            if (!graph || rendersAsChart(graph)) return;

            const existing = document.querySelector('.chart-wrapper[data-id="' + key + '"]') as HTMLElement | null;

            if (checked && !existing) {
                const wrapper = buildChartWrapper(key, graph, currentChartHeight());
                insertWrapperAtOrderPosition(wrapper, key);
                // NEVER renderChart() here — buildChartWrapper() already clones
                // #chart-empty-state-template into the .chart div for a graph that does not
                // render as a chart (rendersAsChart() false).
            } else if (!checked && existing) {
                // D-01 case 2: never tear down a card the user explicitly force-showed — only
                // show-all-inserted empty cards are removed when show-all goes off.
                if (existing.dataset.forced === '1') return;
                const chart = findChartById('chart_' + key);
                if (chart) chart.destroy(); // defensive — empty cards never have a live chart
                existing.remove();
            }
        });

    post('?do=saveShowAll&showAll=' + (checked ? '1' : '0'), {}, (data: any) => {
        if (data.status !== 1) {
            notifier.alert('Failed to save show-all setting');
        }
    });
}

function showInlineStatus(key: string, text: string): HTMLElement | null {
    const checkbox = document.querySelector('.js-graph-visibility[data-id="' + key + '"]');
    const formCheck = checkbox ? (checkbox.closest('.form-check') as HTMLElement | null) : null;
    if (!formCheck) return null;
    const span = document.createElement('span');
    span.className = 'dash-graph-status text-muted small ms-1';
    span.textContent = text;
    formCheck.appendChild(span);
    return span;
}

/** D-07/D-15/D-04: clones the single server-rendered <template> — never a second hand-written
 *  copy of the empty-state markup in TS (zero server/client drift risk, T-218-15 discipline). */
function cloneEmptyStateContent(): DocumentFragment | null {
    const tpl = document.getElementById('chart-empty-state-template') as HTMLTemplateElement | null;
    return tpl ? (tpl.content.cloneNode(true) as DocumentFragment) : null;
}

/**
 * Reproduces the .chart-wrapper[data-id] markup from Home/default.latte's chart loop
 * (chart-drag-handle grip + totals + per-key buttons + ×, .chart div, .chart-resize-handle)
 * so the re-inserted node works identically to a server-rendered one. Built via DOM APIs /
 * textContent — never raw innerHTML of the server-returned graph payload (T-218-15).
 * When `rendersAsChart(graph)` is false, the .chart div gets the `chart-empty` modifier and a
 * cloned #chart-empty-state-template fragment instead of being left empty for renderChart()
 * to fill (Pitfall 2/3 — totals never render alongside an empty card, and Highcharts is never
 * instantiated over one). Phase 282 gap closure (QUICK-06 / GAP 1): a goal-forced graph
 * (`empty_in_range` true but `rendersAsChart(graph)` true) gets a bare `.chart` div instead, so
 * `renderChart()` has a clean mount target.
 */
function buildChartWrapper(key: string, graph: any, height: number): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.className = 'chart-wrapper';
    wrapper.dataset.id = key;
    // Pitfall 3: graph.width is missing on any payload shape that predates this plan — default
    // to full rather than crash the un-hide path.
    const width = graph.width === 'half' ? 'half' : 'full';
    if (width === 'half') wrapper.classList.add('chart-wrapper--half');

    const handle = document.createElement('div');
    handle.className = 'chart-drag-handle';

    const grip = document.createElement('span');
    grip.className = 'chart-drag-grip';
    grip.innerHTML = '&#10303;';
    handle.appendChild(grip);

    // Deliberately the raw empty_in_range flag, NOT rendersAsChart() — an empty range has no
    // totals to print regardless of whether a goal line draws; "does this have totals" and
    // "does this render as a chart" are genuinely different questions here (QUICK-06 / GAP 1).
    if (!graph.empty_in_range && Array.isArray(graph.totals)) {
        graph.totals.forEach((total: any) => {
            const totalEl = document.createElement('div');
            totalEl.className = 'chart-total';

            const label = document.createElement('span');
            label.className = 'chart-total-label';
            label.textContent = total.name + ':';

            const value = document.createElement('span');
            value.className = 'chart-total-value';
            value.textContent = total.value;

            totalEl.appendChild(label);
            totalEl.appendChild(document.createTextNode(' '));
            totalEl.appendChild(value);
            handle.appendChild(totalEl);
        });
    }

    // Phase 262 (HOME-05, Pitfall 3): mirrors Home/default.latte's inline width toggle exactly —
    // same position (before the import/hide buttons), same ms-auto anchor — so a graph un-hidden
    // through the offcanvas renders identically to a server-rendered card, never full-width with
    // a missing toggle until a reload.
    const widthBtn = document.createElement('button');
    widthBtn.type = 'button';
    widthBtn.className = 'btn btn-sm btn-outline-secondary chart-width-btn ms-auto';
    widthBtn.dataset.id = key;
    widthBtn.setAttribute('aria-label', widthButtonAriaLabel(graph.title ?? key, width));
    widthBtn.innerHTML = widthButtonIcon(width);
    handle.appendChild(widthBtn);

    if (key === 'transactions') {
        const importBtn = document.createElement('button');
        importBtn.type = 'button';
        importBtn.className = 'btn btn-sm btn-outline-secondary js-import-csv-open';
        importBtn.setAttribute('data-bs-toggle', 'modal');
        importBtn.setAttribute('data-bs-target', '#import-csv-modal');
        importBtn.setAttribute('aria-label', 'Import CSV');
        importBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">'
            + '<path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5"/>'
            + '<path d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708z"/>'
            + '</svg>';
        handle.appendChild(importBtn);
    }

    const hideBtn = document.createElement('button');
    hideBtn.type = 'button';
    hideBtn.className = 'btn-close chart-hide-btn';
    hideBtn.dataset.id = key;
    hideBtn.setAttribute('aria-label', 'Hide ' + (graph.title ?? key) + ' graph');
    handle.appendChild(hideBtn);

    wrapper.appendChild(handle);

    const chartDiv = document.createElement('div');
    chartDiv.className = 'chart';
    chartDiv.id = 'chart_' + key;
    // Phase 282 gap closure (QUICK-06 / GAP 1): rendersAsChart(), not the raw empty_in_range
    // flag — a goal-forced graph must get a bare .chart div for renderChart() to mount into.
    if (!rendersAsChart(graph)) {
        chartDiv.classList.add('chart-empty');
        const emptyContent = cloneEmptyStateContent();
        if (emptyContent) chartDiv.appendChild(emptyContent);
        // Empty cards have no Highcharts to draw the chart title, so label the card
        // with the graph name so the user knows WHICH metric has no data (UAT fix).
        // textContent, never innerHTML of the server-returned payload (T-218-15).
        const emptyTitle = chartDiv.querySelector('.chart-empty-title') as HTMLElement | null;
        if (emptyTitle) emptyTitle.textContent = graph.title ?? key;
        // Phase 262 (MODAL-01 fold, P-15): #chart-empty-state-template renders its click-hint
        // copy unconditionally — it can't know the key at clone time. This function DOES know
        // the key, so strip the hint here for a non-editable key. EDITABLE_CHART_IDS is the
        // SAME allowlist home.ts's card-click listener and updateValue() already enforce,
        // imported rather than re-declared — a third hard-coded copy would let them drift.
        //
        // T-262-18 closure: the cursor/hover affordance is the same promise as the hint copy, so
        // it is added and withheld on exactly the same condition. Mirrors Home/default.latte's
        // server-rendered `chart-empty--editable` gate — this is the client re-render path taken
        // after a hide/un-hide or show-all cycle, and the two must agree or a card silently
        // changes affordance depending on how it got onto the page (the T-262-37 lesson).
        if (EDITABLE_CHART_IDS.includes(key)) {
            chartDiv.classList.add('chart-empty--editable');
        } else {
            const hint = chartDiv.querySelector('.js-empty-click-hint');
            if (hint) hint.remove();
        }
    }
    wrapper.appendChild(chartDiv);

    const resizeHandle = document.createElement('div');
    resizeHandle.className = 'chart-resize-handle';
    resizeHandle.setAttribute('role', 'slider');
    resizeHandle.tabIndex = 0;
    resizeHandle.setAttribute('aria-orientation', 'vertical');
    resizeHandle.setAttribute('aria-label', 'Resize all graph heights');
    resizeHandle.setAttribute('aria-valuemin', String(HEIGHT_MIN));
    resizeHandle.setAttribute('aria-valuemax', String(HEIGHT_MAX));
    resizeHandle.setAttribute('aria-valuenow', String(height));
    resizeHandle.setAttribute('aria-valuetext', height + 'px');
    wrapper.appendChild(resizeHandle);

    return wrapper;
}

/**
 * D-15/D-10: inserts the un-hidden wrapper at its saved-order position — immediately before
 * the first existing .chart-wrapper whose key appears LATER than `key` in the server-applied
 * order (Object.keys(data_array), minus the non-graph 'yoy_enabled' key), else appends.
 */
function insertWrapperAtOrderPosition(wrapper: HTMLElement, key: string): void {
    const container = document.querySelector('.col-charts');
    if (!container) return;

    const orderedKeys: string[] = typeof data_array !== 'undefined'
        ? Object.keys(data_array).filter((k) => k !== 'yoy_enabled')
        : [];
    const keyIndex = orderedKeys.indexOf(key);

    let insertBefore: Element | null = null;
    if (keyIndex !== -1) {
        for (let i = keyIndex + 1; i < orderedKeys.length; i++) {
            const candidate = container.querySelector('.chart-wrapper[data-id="' + orderedKeys[i] + '"]');
            if (candidate) {
                insertBefore = candidate;
                break;
            }
        }
    }

    if (insertBefore) {
        container.insertBefore(wrapper, insertBefore);
    } else {
        container.appendChild(wrapper);
    }
}

// ── Reset (D-03) ──────────────────────────────────────────────────────────────

function bindReset(): void {
    const btn = document.getElementById('dash-reset-btn');
    if (!btn) return;

    btn.addEventListener('click', () => {
        post('?do=resetGraphSettings', {}, (data: any) => {
            if (data.status !== 1) {
                notifier.alert('Failed to save layout');
                return;
            }

            applyChartHeight(HEIGHT_DEFAULT);

            // P-11 full-width default — handleResetGraphSettings already cleared graphs.widths
            // server-side (262-07); this purely brings the visible state in line with what the
            // server just wrote, so no separate ?do=saveWidths fires from this path.
            document.querySelectorAll<HTMLElement>('.chart-wrapper').forEach((w) => {
                w.classList.remove('chart-wrapper--half');
            });
            document.querySelectorAll<HTMLInputElement>('.js-graph-width').forEach((radio) => {
                radio.checked = radio.value === 'full';
            });
            document.querySelectorAll<HTMLElement>('.chart-width-btn').forEach((btn) => {
                const key = btn.getAttribute('data-id') ?? '';
                btn.setAttribute('aria-label', widthButtonAriaLabel(titleForKey(key), 'full'));
                btn.innerHTML = widthButtonIcon('full');
            });
            scheduleReflow();

            // Reset clears show_all + forced (persisted server-side above) — mirror it client-side:
            // uncheck the show-all switch and tear down every empty-state card (show-all AND
            // force-shown), returning empty graphs to their auto-hidden default.
            const showAllToggle = document.querySelector('.js-show-all-toggle') as HTMLInputElement | null;
            if (showAllToggle) showAllToggle.checked = false;
            document.querySelectorAll<HTMLElement>('.chart-wrapper').forEach((w) => {
                if (w.querySelector('.chart.chart-empty')) w.remove();
            });

            // Re-fetch + re-render every previously-hidden graph on demand (D-15) — no reload.
            // forcePersist=false: an empty graph stays auto-hidden (reset default), never re-forced.
            const toUnhide: { key: string; title: string }[] = [];
            document.querySelectorAll<HTMLInputElement>('.js-graph-visibility').forEach((checkbox) => {
                if (!checkbox.checked) {
                    const key = checkbox.getAttribute('data-id');
                    const title = graphTitleFor(checkbox, key ?? '');
                    checkbox.checked = true;
                    if (key) toUnhide.push({ key, title });
                }
            });
            // 218-REVIEW IN-04: skipSave=true — resetGraphSettings already persisted the
            // empty hidden set server-side, so this batch must not fire one saveHidden()
            // POST per un-hidden graph (was N redundant writes for N previously-hidden graphs).
            toUnhide.forEach(({ key, title }) => unhideGraph(key, title, false, true));
        });
    });
}
